import { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Table, Pagination } from 'react-bootstrap';
import PageHeader1 from '../../components/common/PageHeader1';
import apiClient from "../../utils/api";
import { toast } from 'react-toastify';

function ImportTicketsList() {
    const [importedTickets, setImportedTickets] = useState([]);
    const [csvFile, setCsvFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [perPage] = useState(10);

    useEffect(() => {
        fetchImportedTickets();
    }, [currentPage]);

    const fetchImportedTickets = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/import-tickets?page=${currentPage}&per_page=${perPage}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setImportedTickets(response.data.data);
            setTotalPages(Math.ceil(response.data.total / response.data.per_page));
            setIsLoading(false);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load imported tickets');
            setIsLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setCsvFile(file);

        // Only show preview for CSV files (Excel files can't be easily parsed in browser)
        if (file && file.name.toLowerCase().endsWith('.csv')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                const lines = text.split('\n');
                const headers = lines[0].split(',');
                const data = [];
                
                for (let i = 1; i < Math.min(lines.length, 6); i++) {
                    if (lines[i].trim()) {
                        const row = lines[i].split(',');
                        const rowData = {};
                        headers.forEach((header, index) => {
                            rowData[header.trim()] = row[index] ? row[index].trim() : '';
                        });
                        data.push(rowData);
                    }
                }
                
                setCsvData(data);
                setShowPreview(true);
            };
            reader.readAsText(file);
        } else {
            setCsvData([]);
            setShowPreview(file ? false : true);  // Don't show preview for non-CSV files
        }
    };

    const handleUpload = async () => {
        if (!csvFile) {
            toast.error('Please select a CSV file');
            return;
        }

        try {
            setIsUploading(true);
            setImportResult(null);
            
            const token = localStorage.getItem('auth_token');
            const formData = new FormData();
            formData.append('csv_file', csvFile);

            const response = await apiClient.post('accounts/import-tickets/import', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                }
            });

            setImportResult(response.data);
            toast.success(`Successfully imported ${response.data.imported_count} tickets`);
            fetchImportedTickets();
            setCsvFile(null);
            setShowPreview(false);
            
            // Reset file input
            document.getElementById('csvFileInput').value = '';
            
        } catch (error) {
            console.error('Error uploading CSV:', error);
            toast.error(error.response?.data?.message || 'Error uploading CSV file');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteTicket = async (ticketId) => {
        if (!window.confirm('Are you sure you want to delete this ticket?')) {
            return;
        }

        try {
            setIsLoading(true);
            const token = localStorage.getItem('auth_token');
            await apiClient.delete(`accounts/import-tickets/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Ticket deleted successfully');
            fetchImportedTickets(); // Refresh the list
        } catch (error) {
            console.error('Error deleting ticket:', error);
            let errorMessage = 'Error deleting ticket';
            
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Import Tickets" />

            <div className="row g-3 mb-3">
                <div className="col-md-12">
                    <Card>
                        <Card.Header>
                            <h6 className="mb-0">Upload CSV File</h6>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>File Import (with Ticket Number and Valet Location)</Form.Label>
                                    <Form.Control 
                                        type="file" 
                                        id="csvFileInput"
                                        accept=".csv,.xlsx,.xls,.txt" 
                                        onChange={handleFileChange} 
                                        disabled={isUploading}
                                    />
                                    <Form.Text className="text-muted">
                                        File must contain 'ticket_number' and 'valet_location' columns (supports CSV, Excel formats)
                                    </Form.Text>
                                </Form.Group>
                                
                                {csvFile && (
                                    <div className="mb-3">
                                        {showPreview && csvData.length > 0 ? (
                                            <>
                                                <h6>CSV Preview (first 5 rows):</h6>
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-bordered">
                                                        <thead>
                                                            <tr>
                                                                {Object.keys(csvData[0] || {}).map((header, i) => (
                                                                    <th key={i}>{header}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {csvData.map((row, i) => (
                                                                <tr key={i}>
                                                                    {Object.values(row).map((value, j) => (
                                                                        <td key={j}>{value}</td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="alert alert-info">
                                                {csvFile.name.toLowerCase().endsWith('.csv') 
                                                    ? "CSV preview not available"
                                                    : "Preview not available for Excel files. Make sure your file contains 'ticket_number' and 'valet_location' columns."}
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                <Button 
                                    variant="primary" 
                                    onClick={handleUpload} 
                                    disabled={!csvFile || isUploading}
                                >
                                    {isUploading ? 'Uploading...' : 'Upload and Import'}
                                </Button>
                            </Form>
                            
                            {importResult && (
                                <Alert 
                                    variant={importResult.errors.length > 0 ? 'warning' : 'success'} 
                                    className="mt-3"
                                >
                                    <p>Import completed: {importResult.imported_count} tickets imported successfully.</p>
                                    {importResult.errors.length > 0 && (
                                        <div>
                                            <p>Errors encountered:</p>
                                            <ul>
                                                {importResult.errors.map((error, i) => (
                                                    <li key={i}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <div className="row g-3">
                <div className="col-md-12">
                    <Card>
                        <Card.Header>
                            <h6 className="mb-0">Imported Tickets</h6>
                        </Card.Header>
                        <Card.Body>
                            {isLoading ? (
                                <p>Loading tickets...</p>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <Table striped hover>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Ticket Number</th>
                                                    <th>Valet Location</th>
                                                    <th>Status</th>
                                                    <th>Imported Date</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {importedTickets.map((ticket) => (
                                                    <tr key={ticket.id}>
                                                        <td>{ticket.id}</td>
                                                        <td>{ticket.ticket_number}</td>
                                                        <td>{ticket.valet_location}</td>
                                                        <td>
                                                            <span className={`badge ${
                                                                ticket.status === 'active' 
                                                                    ? 'bg-success' 
                                                                    : ticket.status === 'used' 
                                                                        ? 'bg-warning' 
                                                                        : 'bg-danger'
                                                            }`}>
                                                                {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                                            </span>
                                                        </td>
                                                        <td>{new Date(ticket.imported_at).toLocaleString()}</td>
                                                        <td>
                                                            <Button 
                                                                variant="danger" 
                                                                size="sm" 
                                                                onClick={() => handleDeleteTicket(ticket.id)}
                                                            >
                                                                <i className="icofont-trash"></i>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {importedTickets.length === 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">No imported tickets found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <div className="d-flex justify-content-center mt-3">
                                        <Pagination>
                                            <Pagination.First 
                                                onClick={() => handlePageChange(1)} 
                                                disabled={currentPage === 1}
                                            />
                                            <Pagination.Prev 
                                                onClick={() => handlePageChange(currentPage - 1)} 
                                                disabled={currentPage === 1}
                                            />
                                            
                                            {[...Array(totalPages)].map((_, idx) => (
                                                <Pagination.Item
                                                    key={idx + 1}
                                                    active={idx + 1 === currentPage}
                                                    onClick={() => handlePageChange(idx + 1)}
                                                >
                                                    {idx + 1}
                                                </Pagination.Item>
                                            ))}
                                            
                                            <Pagination.Next 
                                                onClick={() => handlePageChange(currentPage + 1)} 
                                                disabled={currentPage === totalPages}
                                            />
                                            <Pagination.Last 
                                                onClick={() => handlePageChange(totalPages)} 
                                                disabled={currentPage === totalPages}
                                            />
                                        </Pagination>
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}

export default ImportTicketsList;
